/**
 * The geo (Phase 7 check-in) slice of the wire — office geofences and consent-gated
 * GPS check-ins. WP-W10, consuming the geo API Task 17 already built and tested
 * against `openapi.yaml`'s `Geo` tag.
 *
 * Hand-written to mirror the contract, exactly as `hr.ts`'s own header describes for
 * its slice: transcribe what the screen reads, not the whole 10 000-line document,
 * and this task does not touch `openapi.yaml`. A field the contract marks `nullable`
 * is `T | null` here; a field a request marks optional is optional here —
 * `exactOptionalPropertyTypes` is on, so an absent value and an explicit `null` are
 * different facts (`PRODUCT.md` §4).
 *
 * ── `employee_id` is never a field on the check-in body ─────────────────────────
 *
 * `GeoCheckinRequest` has **no `employee_id` member at all** — `openapi.yaml`:
 * "`employee_id` is ALWAYS the session's own — there is no field on this body that
 * could name someone else." The type below mirrors that by omission, the same
 * discipline `hr.ts`'s `LeaveRequestCreateRequest` keeps for a leave request (see
 * that file's own docblock).
 */

import { qs, type QueryValue } from './paths';

/* ─────────────────────────────────────────────────────── office geofences ── */

/**
 * `OfficeLocation` of `openapi.yaml` — a company's office geofence: a name, a centre
 * point and a radius in metres. `required: [id, name, latitude, longitude, radius_m,
 * is_active, created_at, updated_at]` — every member here is present, never `T | null`.
 */
export interface OfficeLocation {
  readonly id: string;
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly radius_m: number;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `OfficeLocationCreateRequest`. `name`, `latitude` and `longitude` are required;
 * `radius_m` defaults to 150m server-side when absent and `is_active` defaults to
 * `true` — neither is sent unless the caller actually chose one, which is what
 * "optional" means here rather than "sent as `null`".
 */
export interface OfficeLocationCreateRequest {
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly radius_m?: number;
  readonly is_active?: boolean;
}

/** `OfficeLocationListResponse` — a bare `{ data: [...] }`, newest first, no page. */
export interface OfficeLocationListResponse {
  readonly data: readonly OfficeLocation[];
}

export const OFFICE_LOCATIONS_PATH = '/api/v1/geo/office-locations';

/**
 * `PATCH /geo/office-locations/{id}`. `openapi.yaml`'s `OfficeLocationUpdateRequest`
 * (Task T2-C, migration 048) — every field optional, at least one required; a field
 * left out keeps the geofence's current value. `radius_m` is `50..3000`, the range
 * `wp_office_locations_radius_range_ck` enforces — the same range
 * `OfficeLocationCreateRequest`'s create-time `radius_m` uses, so the two endpoints
 * and the CHECK never disagree about what a caller may send.
 *
 * `manage_policies` only: the server answers `403 WORKFORCE_PERMISSION_DENIED` for
 * everyone else. The UI hides the edit/deactivate controls when the session's
 * `permissions` lack `workforce.manage_policies`, but a `403` is still the source of
 * truth — a session that believes it holds the code and does not gets the server's
 * refusal rendered, never a client-side guess standing in for it.
 */
export interface OfficeLocationUpdateRequest {
  readonly name?: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly radius_m?: number;
  readonly is_active?: boolean;
}

/** `PATCH /geo/office-locations/{id}`. */
export function officeLocationPath(id: string): string {
  return `${OFFICE_LOCATIONS_PATH}/${encodeURIComponent(id)}`;
}

/* ────────────────────────────────────────────────────────────── check-ins ── */

/**
 * `GeoCheckinRequest`. `latitude`, `longitude` and `consent_ack` are required;
 * `accuracy_m` and `device_id` are present-or-absent. `consent_ack` is typed as a
 * plain `boolean` rather than the literal `true`, because sending `false` is a real,
 * shape-valid request the server refuses with `409 WORKFORCE_CONFLICT` — narrowing
 * the type here would make that refusal untestable through this client.
 */
export interface GeoCheckinRequest {
  /** Optional since migration 055, and optional TOGETHER with `longitude`: an
   * employee who has exercised the PDPL's location opt-out sends neither. One
   * without the other is a `400`, and so is omitting both while NOT opted out. */
  readonly latitude?: number;
  readonly longitude?: number;
  readonly accuracy_m?: number;
  readonly device_id?: string;
  readonly consent_ack: boolean;
}

/** The nested `office` member of a `GeoCheckin` — `null` when none was measured against. */
export interface GeoCheckinOffice {
  readonly id: string;
  readonly name: string;
}

/**
 * `GeoTrust` — the check-in trust verdict, migration 047 (`src/modules/geo/trust.ts`).
 * A FLAG for a manager to review, never a reason a check-in was refused: every
 * verdict gets the same `201` from `POST /geo/checkin`, and `trust`/`trust_reasons`
 * only appear once the row is read back. The server ranks `suspect` worse than
 * `unverified` worse than `trusted` when it computes the value, but nothing on this
 * side of the wire treats one as louder than another to render — `TeamCheckinsPanel`
 * shows all three at the same neutral severity, the research guardrail `trust.ts`'s
 * own header states: "a FLAG for human review, never an auto-reject".
 */
export type GeoTrust = 'trusted' | 'suspect' | 'unverified';

/**
 * `GeoTrustReason` — the machine check that fired, named plainly rather than
 * accusingly: a mock-location report, an accuracy reading over the company's
 * policy, no accuracy reported at all, or an implied travel speed over policy
 * against the employee's own previous check-in (`trust.ts`'s three checks).
 */
export type GeoTrustReason =
  | 'mock_location'
  | 'low_accuracy'
  | 'no_accuracy'
  | 'impossible_travel'
  /** Migration 055 — the employee has opted out of location capture, so no
   * coordinates were stored. Always beside `trust: 'unverified'` and never
   * `suspect`: exercising a statutory right is not suspicious behaviour, and this
   * screen renders it at the same neutral severity as every other verdict. */
  | 'location_opted_out';

/**
 * `GeoCheckin` of `openapi.yaml` — a stored check-in with its geofence decision.
 * Every member the contract marks `required` is written out here, `T | null` where
 * the schema allows it, because the server sends the key rather than omitting it.
 *
 * `trust`, `trust_reasons` and `travel_speed_kmh` are migration 047 (Task P1,
 * merged) — `renderGeoCheckin` (`geo-service.ts`) always sends the three together,
 * so none of them is optional here.
 */
export interface GeoCheckin {
  readonly id: string;
  readonly employee_id: string;
  readonly device_id: string | null;
  /** `null` (migration 055) when the employee had opted out of location capture at
   * the moment of the check-in: the server discarded the coordinates before storing
   * the row. `latitude`, `longitude` and `within_geofence` are all null or all set
   * together, so one null is enough to know the row carries no location. */
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracy_m: number | null;
  readonly office: GeoCheckinOffice | null;
  readonly within_geofence: boolean | null;
  readonly distance_m: number | null;
  readonly checked_in_at: string;
  readonly consent_ack: boolean;
  readonly created_at: string;
  readonly trust: GeoTrust;
  readonly trust_reasons: readonly GeoTrustReason[];
  readonly travel_speed_kmh: number | null;
}

/** `GeoCheckinListResponse` — a bare `{ data: [...] }`, most recent first, no page. */
export interface GeoCheckinListResponse {
  readonly data: readonly GeoCheckin[];
}

export const GEO_CHECKIN_PATH = '/api/v1/geo/checkin';
export const GEO_CHECKINS_PATH = '/api/v1/geo/checkins';

/**
 * `GET /geo/checkins?employee_id=…&trust=…`.
 *
 * `employeeId` omitted means the caller's own history — needs no `X-Access-Reason`
 * and writes no `wp_data_access_logs` row. A named `employeeId` is the view ladder
 * plus a MANDATORY reason, which is a request **header** and therefore travels
 * separately, through `useApiQuery`'s `headers` option (`access/reason.ts`) — a path
 * builder is the wrong place for it, the same rule `paths.ts`'s own WP-L5 block
 * states for `GET /alerts`.
 *
 * `trust` (migration 047) narrows to one verdict — `TeamCheckinsPanel`'s "chỉ hiện
 * nghi ngờ" filter passes `'suspect'`. Omitted means every verdict, same as before
 * this filter existed.
 */
export function geoCheckinsPath(employeeId?: string, trust?: GeoTrust): string {
  const params: Readonly<Record<string, QueryValue>> = { employee_id: employeeId, trust };
  return `${GEO_CHECKINS_PATH}${qs(params)}`;
}

/* ───────────────────────────────────────────────────────────────── geo policy ── */

/**
 * `GeoPolicy.outside_geofence_mode` — `allow_flag` (the default) stores an
 * out-of-fence check-in with `within_geofence: false`; `block` refuses it,
 * `400 WORKFORCE_GEO_OUTSIDE_GEOFENCE`, but only when there IS an active office to
 * be outside of. Never derived from `trust` — a `suspect` check-in is never
 * blocked, in either mode.
 */
export type GeoOutsideGeofenceMode = 'allow_flag' | 'block';

/**
 * `GeoPolicy` of `openapi.yaml` (Task T2-C, migrations 042/047/048) — this
 * company's `wp_geo_policy` row. A company with no row yet reads the closed
 * defaults (`geo_checkin_enabled: false`, `max_accuracy_m: 100`,
 * `max_travel_kmh: 900`, `outside_geofence_mode: 'allow_flag'`) with
 * `updated_by`/`updated_at` both `null`, never inferred — `GET /geo/policy` never
 * inserts a row to answer a read.
 *
 * `manage_policies` only, scope `company`: the server answers
 * `403 WORKFORCE_PERMISSION_DENIED` for everyone else. `GeoScreen` hides the panel
 * this type feeds when the session's `permissions` lack
 * `workforce.manage_policies`, but a `403` on this read (a session that believes it
 * holds the code and does not) is still the source of truth and is rendered as the
 * refusal it is, never swallowed.
 */
export interface GeoPolicy {
  readonly geo_checkin_enabled: boolean;
  readonly max_accuracy_m: number;
  readonly max_travel_kmh: number;
  readonly outside_geofence_mode: GeoOutsideGeofenceMode;
  readonly updated_by: string | null;
  readonly updated_at: string | null;
}

/**
 * `PUT /geo/policy`. `openapi.yaml`'s `GeoPolicyUpdateRequest` (Task T2-C,
 * migration 048) — every field optional, at least one required; a field left out
 * keeps the row's current value (or the column default, for a company with no row
 * yet). `max_accuracy_m` is `5..1000`, `max_travel_kmh` is `50..2000` — the same
 * bounds the server's own zod schema enforces.
 */
export interface GeoPolicyUpdateRequest {
  readonly geo_checkin_enabled?: boolean;
  readonly max_accuracy_m?: number;
  readonly max_travel_kmh?: number;
  readonly outside_geofence_mode?: GeoOutsideGeofenceMode;
}

export const GEO_POLICY_PATH = '/api/v1/geo/policy';

/** `GET /geo/policy` / `PUT /geo/policy` — one fixed path, no id to encode. */
export function geoPolicyPath(): string {
  return GEO_POLICY_PATH;
}
